import { useState } from 'react';
import { Device } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import {
  CreateResponse,
  ListResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/sip-profiles/v1/sip_profiles.pb';
import { useQueryClient } from 'react-query';
import { CallGroupApi, CallGroupTypes } from '@frontend/api-call-group';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { useTranslation } from '@frontend/i18n';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { WeaveLocation } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Heading, MultiStep, MultiStepModalProps, TextField, useAlert, useFormField } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { AssignDeviceContent } from '../../common/assign-device-content';
import { LabelsCard } from '../../common/labels-card';
import { SettingsCard } from '../../common/settings-card';

type CreateCallGroupModalProps = {
  tenantLocation: SettingsTenantLocation;
  devices: CallGroupTypes.SipProfileType[];
  callForwardingNumbers: ForwardingNumberTypes.FwdNumberModel[];
  modalProps: MultiStepModalProps;
  onSuccess?: (data: CreateResponse) => void;
};

export const CreateCallGroupModal = ({
  tenantLocation,
  devices,
  callForwardingNumbers,
  modalProps,
  onSuccess,
}: CreateCallGroupModalProps) => {
  const { t } = useTranslation('phone');
  const onClose = modalProps.modalProps.onClose;
  const isUnifyLocation = tenantLocation.locationType === 'unify';
  const alert = useAlert();
  const queryClient = useQueryClient();
  const tenantId: string = tenantLocation?.phoneTenantId ?? '';
  const defaultTimeout = 18000;
  const invalidateQueries = useInvalidateQueries();

  const [callGroup, setCallGroup] = useState<CreateResponse>(() => ({
    callGroupId: '',
    tenantId: '',
    locationIds: [],
    name: '',
    ringtone: '',
    devices: [],
  }));
  const { callGroupId, ...payload } = callGroup;
  const textFieldProps = useFormField({ type: 'text', value: callGroup.name }, [callGroup.name]);
  const { navigate } = useSettingsNavigate();

  const stepTitleText: { [key: number]: string } = {
    1: t('Call Group Name'),
    2: t('Assign Devices'),
    3: t('Ringtone'),
  };

  if (isUnifyLocation) {
    stepTitleText[4] = t('Locations');
  }

  const { mutate: createCallGroup } = useMutation(
    (payload: CallGroupTypes.CreateCallGroupRequest) => {
      return CallGroupApi.createCallGroup(payload);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(queryKeys.callGroup(callGroup.callGroupId ?? ''), data);
        queryClient.setQueryData<ListResponse>(queryKeys.listCallGroups(tenantLocation?.phoneTenantId ?? ''), (old) => {
          const updated = {
            callGroups: [
              ...(old?.callGroups ?? []),
              // Add the new call group to the list.
              // NOTE: the data returned from the API in the mutation response is not the same as the data in the list
              // and needs to be transformed.
              {
                devicesCount: data.devices.length,
                id: data.callGroupId,
                locationIds: data.locationIds,
                name: data.name,
                tenantId: data.tenantId,
              },
            ],
          };

          return updated;
        });
        invalidateQueries(queryKeys.listCallGroups(tenantId));
        alert.success({
          message: t('Call Group created successfully.'),
          action: {
            label: t('Go to Call Group'),
            onClick: () => navigate({ to: '/phone/call-groups/:id', params: { id: data.callGroupId } }),
          },
        });
        onSuccess?.(data);
      },
      onError: () => {
        alert.error(t('System failed to create Call Group. Please try again.'));
      },
    }
  );

  const locationsNames =
    tenantLocation?.children?.reduce((acc: Record<string, string>, item: Partial<WeaveLocation>) => {
      if (item.locationId && item.name) {
        acc[item.locationId] = item.name;
      }
      return acc;
    }, {}) ?? {};

  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedMobiles, setSelectedMobiles] = useState<string[]>([]);
  const [selectedForwardingNumbers, setSelectedForwardingNumbers] = useState<string[]>([]);

  const forwardingNumbers = selectedForwardingNumbers
    .map((id) => {
      return {
        callLegId: '',
        callGroupId: '',
        delayStart: 0,
        timeout: defaultTimeout,
        forwardingNumber: {
          forwardingNumberId: id,
        },
      };
    })
    .filter(Boolean);

  const deskPhones = selectedDevices
    .map((profileID) => {
      const deviceData = devices?.find((device) => device.sipProfileId === profileID);
      if (!deviceData) {
        return null;
      }

      const { device } = deviceData;
      const phoneType = device?.deviceType;

      if (phoneType === DeviceType_Enum.DESK_PHONE) {
        return {
          callLegId: '',
          callGroupId: '',
          delayStart: 0,
          timeout: defaultTimeout,
          deskPhone: {
            sipProfileId: profileID,
          },
        };
      } else if (phoneType === DeviceType_Enum.SOFTPHONE) {
        return {
          callLegId: '',
          callGroupId: '',
          delayStart: 0,
          timeout: defaultTimeout,
          softPhone: {
            sipProfileId: profileID,
          },
        };
      } else {
        return null;
      }
    })
    .filter(Boolean);

  const mobilePhones = selectedMobiles
    .map((profileID) => {
      const deviceData = devices?.find((device) => device.sipProfileId === profileID);
      if (!deviceData) {
        return null;
      }

      const { device } = deviceData;
      const phoneType = device?.deviceType;

      if (phoneType === DeviceType_Enum.MOBILE_APP) {
        return {
          callLegId: '',
          callGroupId: '',
          delayStart: 0,
          timeout: defaultTimeout,
          mobilePhone: {
            sipProfileId: profileID,
          },
        };
      } else {
        return null;
      }
    })
    .filter(Boolean);

  const combinedDevices = [...deskPhones, ...mobilePhones, ...forwardingNumbers] as Device[];

  const handleClose = () => {
    setSelectedDevices([]);
    setSelectedMobiles([]);
    setSelectedForwardingNumbers([]);
    setCallGroup({
      callGroupId: '',
      tenantId: '',
      locationIds: [],
      name: '',
      ringtone: '',
      devices: [],
    });
    onClose();
  };

  return (
    <MultiStep.Modal minWidth={800} {...modalProps} onCancel={handleClose}>
      {/* Step 1: Call Group Name */}
      <MultiStep.Step
        id='name'
        header={{ title: t('Create Call Group') }}
        label={t('Call Group Name')}
        isValid={textFieldProps.value !== ''}
        footer={{ secondary: t('Cancel'), onClickSecondary: handleClose }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Heading level={3}>{t('Call Group Name')}</Heading>
          <div css={{ width: '318px', paddingLeft: theme.spacing(0.25) }}>
            <TextField
              {...textFieldProps}
              onChange={(e) => {
                textFieldProps.onChange(e);
                setCallGroup({
                  ...callGroup,
                  // @ts-expect-error - event types are not correct
                  name: e.target.value,
                  tenantId: tenantId,
                });
              }}
              label={t('Call Group Name')}
              name='name'
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 2: Assign Devices */}
      <MultiStep.Step
        css={{ maxWidth: '535px' }}
        id='devices'
        header={{ title: t('Create Call Group') }}
        label={t('Assign Devices')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: handleClose,
          onClickPrimary: () => {
            setCallGroup({ ...callGroup, devices: combinedDevices });
            if (!isUnifyLocation) {
              createCallGroup({ ...payload, devices: combinedDevices });
              handleClose();
            } else {
              modalProps.goNextStep();
            }
          },
          helpText:
            combinedDevices.length > 0 ? t('{{count}} devices selected', { count: combinedDevices.length }) : undefined,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: 0,
            },
            padding: theme.spacing(0.25),
          }}
        >
          <SettingsCard>
            <AssignDeviceContent
              callForwardingNumbers={callForwardingNumbers}
              callGroup={callGroup}
              devices={devices}
              locationsNames={locationsNames}
              ringtone={callGroup.ringtone}
              setSelectedDevices={setSelectedDevices}
              setSelectedMobiles={setSelectedMobiles}
              setSelectedForwardingNumbers={setSelectedForwardingNumbers}
              title={t('Assign Devices')}
              subtitle={t('Select the devices that will be assigned to this call group.')}
              isMultiLocation={tenantLocation?.locationType !== 'single'}
              setCallGroup={setCallGroup}
            />
          </SettingsCard>
        </div>
      </MultiStep.Step>

      {/* Step 4: Location (only if isUnify) */}
      {isUnifyLocation && (
        <MultiStep.Step
          id='location'
          header={{ title: t('Create Call Group') }}
          label={t('Location')}
          footer={{
            secondary: t('Cancel'),
            onClickSecondary: handleClose,
            onClickPrimary: () => {
              createCallGroup(payload);
              handleClose();
            },
          }}
        >
          <div
            css={{
              '> div': {
                boxShadow: 'none',
                padding: 0,
              },
            }}
          >
            {callGroup.locationIds && <LabelsCard callGroup={callGroup} setCallGroup={setCallGroup} />}
          </div>
        </MultiStep.Step>
      )}
    </MultiStep.Modal>
  );
};
